/**
 * AtlasWaterAudioModule — the sound of water (2026-09-20).
 *
 * Rivers, waterfalls, lakes and the sea were SILENT: no loop asset existed. With
 * the loops generated (tools/asset-pipeline/gen-world-sounds.mjs) this module
 * places them; the policy is pure (lib/audio/water_audio.ts):
 *
 *   rivers      ONE emitter sliding along the nearest point of the nearest
 *               polyline (the spline technique) — a brook chatters high and
 *               quiet, a wide river is deeper and carries; a still reach (a
 *               river crossing a lake) is not a source
 *   waterfalls  the two loudest within 750 m, each a close layer (bright spray)
 *               crossfading to a far layer (low rumble) with distance
 *   shores      a fan of rays finds the nearest sea and the nearest lake: waves
 *               wash and answer the wind's sea state; a lake only laps
 *   envelopment as you reach any of them a 2D copy of the same loop fades in
 *               over the located one — wading across a river never flips it
 *               from one ear to the other
 *
 * Under water, in space, aboard the sky-ship and behind the loading gate the
 * whole family is silent (the ambience module's under-water bed speaks there).
 * A clip that is not installed is a loop that never sounds — wiring may land
 * before its audio, as ever. System `AtlasWaterAudio`, Animation / 51, 8 Hz.
 */
import type { App, IModule } from 'enginex';
import { makeSystem, Phase } from '../engine-compat.js';
import { atlas } from '../state.js';
import { audioRuntime, type LoopHandle } from '../lib/audio/runtime.js';
import { waterKindAt, waterSeaAmp } from '../lib/water_fx.js';
import {
  WATER_KEYS, RIVER_RANGE_M, envelop, fallMix, pickFalls, riverEmitter, riverMix, scanShore, shoreMix,
  type RiverSample, type ShoreHit, type StillKind,
} from '../lib/audio/water_audio.js';
import type { AtlasRiverModule } from './AtlasRiverModule.js';

const TICK_S = 0.125;
/** The river lines are re-gathered after the player has moved this far (a whole-network scan — never per frame). */
const RESCAN_M = 30;
const SHORE_EVERY_S = 1;
const RAMP_S = 0.45;

/** A located loop + its enveloping 2D twin. */
interface Pair { pos: LoopHandle; around: LoopHandle }

export class AtlasWaterAudioModule implements IModule {
  readonly name = 'AtlasWaterAudio';
  private app: App | null = null;
  private rivers: AtlasRiverModule | null | undefined;
  private acc = 0;
  private shoreAcc = 0;
  private lines: RiverSample[][] = [];
  private linesAt: { x: number; z: number; world: bigint | null } | null = null;
  private shore: Partial<Record<StillKind, ShoreHit>> = {};
  private readonly pairs = new Map<string, Pair>();
  private readonly fallScopes = new Set<string>();
  private diag: Record<string, unknown> = {};

  init(app: App): void {
    this.app = app;
    app.world.addSystem(makeSystem('AtlasWaterAudio', Phase.Animation, 51, (dt) => this.tick(dt)));
    (window as unknown as { __waterAudio?: () => unknown }).__waterAudio = () => ({ ...this.diag, lines: this.lines.length, installed: Object.fromEntries(Object.values(WATER_KEYS).map((k) => [k, audioRuntime().available(k)])) });
  }

  private pair(scope: string, key: string, priority: number): Pair {
    let p = this.pairs.get(`${scope}|${key}`);
    if (!p) {
      const rt = audioRuntime();
      p = {
        pos: rt.loop(`${scope}:pos`, key, { priority, distance: 'caller', occlude: false }),
        around: rt.loop(`${scope}:around`, key, { priority }),
      };
      this.pairs.set(`${scope}|${key}`, p);
    }
    return p;
  }
  private drive(scope: string, key: string, priority: number, gain: number, rate: number, near: number, at: { x: number; y: number; z: number } | null): void {
    const p = this.pair(scope, key, priority);
    const g = envelop(gain, near);
    if (at) p.pos.at(at);
    p.pos.set(at ? g.positional : 0, rate, RAMP_S);
    p.around.set(at ? g.around : gain, rate, RAMP_S);
  }
  private hush(scope: string, key: string): void {
    const p = this.pairs.get(`${scope}|${key}`);
    if (p) { p.pos.set(0, 1, RAMP_S); p.around.set(0, 1, RAMP_S); }
  }

  private tick(dt: number): void {
    this.acc += dt; this.shoreAcc += dt;
    if (this.acc < TICK_S) return;
    this.acc = 0;
    const rt = audioRuntime();
    if (!rt.ctx) return;
    const silent = atlas.currentWorldId === null || atlas.loadingGate || atlas.spaceFlight || atlas.submerged
      || (typeof atlas.interiorId === 'string' && atlas.interiorId.startsWith('skyship:'));
    if (silent) { for (const p of this.pairs.values()) { p.pos.set(0, 1, RAMP_S); p.around.set(0, 1, RAMP_S); } this.diag = { silent: true }; return; }
    if (this.rivers === undefined) this.rivers = (this.app?.getModule('AtlasRiverModule') as AtlasRiverModule | undefined) ?? undefined;
    const pose = rt.listenerPose();
    const px = pose.x, py = pose.y, pz = pose.z;
    // Indoors the water outside is a wall away: a flat −8 dB (the obstruction ray is off for these — a river is a line, not a point to ray-test).
    const indoor = typeof atlas.interiorId === 'string' && atlas.interiorId !== '' ? 0.4 : 1;

    // ── the river: one emitter on the nearest line ──
    const onCommons = atlas.currentWorldId === atlas.commonsWorldId;
    if (this.rivers && onCommons) {
      if (!this.linesAt || this.linesAt.world !== atlas.currentWorldId || Math.hypot(px - this.linesAt.x, pz - this.linesAt.z) > RESCAN_M) {
        this.lines = this.rivers.riverLinesNear(px, pz, RIVER_RANGE_M + RESCAN_M + 20);
        this.linesAt = { x: px, z: pz, world: atlas.currentWorldId };
      }
    } else { this.lines = []; this.linesAt = null; }
    let river: ReturnType<typeof riverEmitter> = null;
    for (const line of this.lines) { const e = riverEmitter(line, px, pz); if (e && (!river || e.edgeM < river.edgeM)) river = e; }
    if (river && river.edgeM < RIVER_RANGE_M) {
      const m = riverMix(river.edgeM, river.width);
      this.drive('water:river', WATER_KEYS.river, 46, m.gain * indoor, m.rate, m.near, { x: river.x, y: river.y, z: river.z });
    } else this.hush('water:river', WATER_KEYS.river);

    // ── waterfalls: the two loudest, a close layer and a far layer each ──
    const falls = this.rivers && onCommons ? pickFalls(this.rivers.soundFalls(), px, py, pz, 2) : [];
    const live = new Set<string>();
    for (const f of falls) {
      const scope = `water:fall:${f.site.key}`;
      live.add(scope);
      this.fallScopes.add(scope);
      const at = { x: f.site.x, y: f.site.y + f.site.drop * 0.4, z: f.site.z };
      this.drive(scope, WATER_KEYS.fallClose, 48, f.mix.close * indoor, f.mix.rate, f.mix.near, at);
      this.drive(scope, WATER_KEYS.fallFar, 44, f.mix.far * indoor, f.mix.rate, 0, at);
    }
    for (const scope of this.fallScopes) {
      if (live.has(scope)) continue;
      this.hush(scope, WATER_KEYS.fallClose); this.hush(scope, WATER_KEYS.fallFar);
      // Forget a fall left far behind (its loops have virtualised by now): the map must not grow with the journey.
      const p = this.pairs.get(`${scope}|${WATER_KEYS.fallClose}`);
      if (p && this.fallScopes.size > 6) { for (const k of [WATER_KEYS.fallClose, WATER_KEYS.fallFar]) this.pairs.delete(`${scope}|${k}`); rt.dropScope(`${scope}:pos`); rt.dropScope(`${scope}:around`); this.fallScopes.delete(scope); }
    }

    // ── shores: the nearest sea, the nearest lake ──
    if (this.shoreAcc >= SHORE_EVERY_S) { this.shoreAcc = 0; this.shore = scanShore(px, pz, waterKindAt); }
    for (const kind of ['sea', 'lake'] as const) {
      const hit = this.shore[kind], key = kind === 'sea' ? WATER_KEYS.sea : WATER_KEYS.lake;
      if (!hit) { this.hush(`water:${kind}`, key); continue; }
      const m = shoreMix(hit.d, kind, waterSeaAmp());
      this.drive(`water:${kind}`, key, 45, m.gain * indoor, m.rate, m.near, { x: hit.x, y: py, z: hit.z });
    }
    this.diag = {
      river: river ? { edgeM: +river.edgeM.toFixed(1), width: +river.width.toFixed(1), ...riverMix(river.edgeM, river.width) } : null,
      falls: falls.map((f) => ({ key: f.site.key, dist: +f.dist.toFixed(0), drop: +f.site.drop.toFixed(1), ...fallMix(f.dist, f.site.drop, f.site.width) })),
      shore: this.shore, seaAmp: +waterSeaAmp().toFixed(2), indoor,
    };
  }

  dispose(): void {
    const rt = audioRuntime();
    for (const key of this.pairs.keys()) { const scope = key.split('|')[0]; rt.dropScope(`${scope}:pos`); rt.dropScope(`${scope}:around`); }
    this.pairs.clear();
  }
}
